import { getPlatformAdapter } from "./platform";

export type LaunchIntent =
  | { kind: "drill" }
  | { kind: "deck"; deckId: string }
  | { kind: "invite"; token: string }
  | { kind: "growth-share"; code: string };

type StoredLaunchIntent = {
  intent: LaunchIntent;
  savedAt: number;
};

export const PENDING_LAUNCH_INTENT_KEY = "lexibit.pendingLaunchIntent.v1";
export const PENDING_LAUNCH_INTENT_EVENT = "lexibit:pending-launch-intent";

const MAX_PENDING_AGE_MS = 30 * 24 * 60 * 60 * 1_000;
const SAFE_DECK_ID = /^[A-Za-z0-9_-]{1,128}$/;
const SAFE_INVITE_TOKEN = /^[A-Za-z0-9_-]{16,256}$/;
const SAFE_SHARE_CODE = /^[A-Za-z0-9_-]{22}$/;

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Accepts a raw token, a `/join/<token>` path, a full invite URL, or
 * the provider launch value `invite_<token>`. */
export function normalizeInviteToken(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = safeDecode(value.trim());
  if (!trimmed) return null;

  const launchMatch = /^invite_([A-Za-z0-9_-]{16,256})$/.exec(trimmed);
  if (launchMatch) return launchMatch[1];

  const pathMatch = /(?:^|\/)join\/([A-Za-z0-9_-]{16,256})(?:[/?#]|$)/.exec(trimmed);
  if (pathMatch) return pathMatch[1];

  return SAFE_INVITE_TOKEN.test(trimmed) ? trimmed : null;
}

/** Converts provider-neutral launch data into a closed, safe route intent. */
export function parseLaunchIntent(startParam: string | null | undefined): LaunchIntent | null {
  if (!startParam) return null;
  if (startParam === "drill") return { kind: "drill" };
  const shareMatch = /^share_([A-Za-z0-9_-]{22})$/.exec(startParam);
  if (shareMatch) return { kind: "growth-share", code: shareMatch[1] };

  const deckMatch = /^deck_([A-Za-z0-9_-]{1,128})$/.exec(startParam);
  if (deckMatch && SAFE_DECK_ID.test(deckMatch[1])) {
    return { kind: "deck", deckId: deckMatch[1] };
  }

  // Campaign tags (`c_<channel>`) are analytics-only and open the regular start.
  if (startParam.startsWith("c_")) return null;

  const token = normalizeInviteToken(startParam);
  return token ? { kind: "invite", token } : null;
}

export function launchIntentRoute(intent: LaunchIntent): string {
  switch (intent.kind) {
    case "drill":
      return "/drill";
    case "deck":
      return `/decks/${intent.deckId}`;
    case "invite":
      return `/join/${intent.token}`;
    case "growth-share":
      return `/s/${intent.code}`;
  }
}

function isLaunchIntent(value: unknown): value is LaunchIntent {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<LaunchIntent> & { kind?: unknown };
  if (candidate.kind === "drill") return true;
  if (candidate.kind === "deck") {
    return typeof candidate.deckId === "string" && SAFE_DECK_ID.test(candidate.deckId);
  }
  if (candidate.kind === "invite") {
    return typeof candidate.token === "string" && SAFE_INVITE_TOKEN.test(candidate.token);
  }
  if (candidate.kind === "growth-share") {
    return typeof candidate.code === "string" && SAFE_SHARE_CODE.test(candidate.code);
  }
  return false;
}

export function readPendingLaunchIntent(): LaunchIntent | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(PENDING_LAUNCH_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLaunchIntent>;
    if (
      !isLaunchIntent(parsed.intent) ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > MAX_PENDING_AGE_MS
    ) {
      sessionStorage.removeItem(PENDING_LAUNCH_INTENT_KEY);
      return null;
    }
    return parsed.intent;
  } catch {
    sessionStorage.removeItem(PENDING_LAUNCH_INTENT_KEY);
    return null;
  }
}

export function savePendingLaunchIntent(intent: LaunchIntent): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const value: StoredLaunchIntent = { intent, savedAt: Date.now() };
    sessionStorage.setItem(PENDING_LAUNCH_INTENT_KEY, JSON.stringify(value));
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent<LaunchIntent | null>(PENDING_LAUNCH_INTENT_EVENT, { detail: intent }),
      );
    }
  } catch {
    // Private browsing can disable storage. The current navigation still works.
  }
}

export function clearPendingLaunchIntent(expected?: LaunchIntent): void {
  if (typeof sessionStorage === "undefined") return;
  if (expected) {
    const current = readPendingLaunchIntent();
    if (!current || launchIntentRoute(current) !== launchIntentRoute(expected)) return;
  }
  sessionStorage.removeItem(PENDING_LAUNCH_INTENT_KEY);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<LaunchIntent | null>(PENDING_LAUNCH_INTENT_EVENT, { detail: null }),
    );
  }
}

/** Reads the start parameter from the active Telegram/VK/MAX/web adapter.
 * Launch data is navigation input only and is never treated as auth proof. */
export function capturePlatformLaunchIntent(): LaunchIntent | null {
  const intent = parseLaunchIntent(getPlatformAdapter().getStartParam());
  if (intent) savePendingLaunchIntent(intent);
  return intent;
}
